import {useMemo} from 'react';
import {useWindowDimensions} from 'react-native';

/**
 * Window-size classes — the single source of truth for "how big is the window
 * we render into?", shared by every responsive screen, modal and navigator.
 *
 * The classes follow the Material 3 / Android adaptive-layout guidance, which is
 * what foldables are built around. A fold or unfold is NOT a rotation: it is a
 * live window resize (a Galaxy Z Fold goes from ~344dp wide on the cover screen
 * to ~700-840dp on the inner screen, a Pixel Fold from ~380dp to ~700dp), and
 * the inner screens are close to square. Android multi-window / split-screen and
 * desktop/web windows resize the same way. So:
 *
 *  - never cache `Dimensions.get(...)` at import time (it is stale after the
 *    first fold) — read {@link useWindowSizeClass} / `useWindowDimensions()`;
 *  - never hard-code a phone/tablet number in a screen — import the constants
 *    here (a 768dp "tablet" cut-off left every unfolded Fold/Pixel Fold in
 *    portrait, ~690-750dp, on the stretched phone layout).
 *
 * Width classes (dp):  compact < 600 <= medium < 840 <= expanded.
 * Height classes (dp): compact < 480 <= medium < 900 <= expanded.
 */

/** Width (dp) at/above which the window is "medium": unfolded foldables, 7-8" tablets. @type {number} */
export const WINDOW_MEDIUM_MIN = 600;

/** Width (dp) at/above which the window is "expanded": large tablets, landscape foldables, desktop/web. @type {number} */
export const WINDOW_EXPANDED_MIN = 840;

/** Height (dp) at/above which the window is "medium" tall (phone landscape stays below this). @type {number} */
export const WINDOW_HEIGHT_MEDIUM_MIN = 480;

/** Height (dp) at/above which the window is "expanded" tall. @type {number} */
export const WINDOW_HEIGHT_EXPANDED_MIN = 900;

/** Readable max width (dp) of forms, detail pages and other single-column content on wide windows. @type {number} */
export const CONTENT_MAX_WIDTH_FORM = 640;

/** Max width (dp) of dashboards / list grids on very wide windows. @type {number} */
export const CONTENT_MAX_WIDTH_WIDE = 1180;

/** Default max width (dp) of a centred dialog / popup card. @type {number} */
export const MODAL_MAX_WIDTH = 560;

/**
 * Normalises a measurement: finite and non-negative, otherwise 0.
 *
 * @param {*} value Raw width/height.
 * @returns {number}
 */
const toSize = (value) => {
    const n = Number(value);

    return Number.isFinite(n) && n > 0 ? n : 0;
};

/**
 * Width class of a window.
 *
 * @param {number} width Window width in dp.
 * @returns {'compact'|'medium'|'expanded'}
 */
export const getWidthClass = (width) => {
    const w = toSize(width);

    if (w >= WINDOW_EXPANDED_MIN) {
        return 'expanded';
    }

    return w >= WINDOW_MEDIUM_MIN ? 'medium' : 'compact';
};

/**
 * Height class of a window.
 *
 * @param {number} height Window height in dp.
 * @returns {'compact'|'medium'|'expanded'}
 */
export const getHeightClass = (height) => {
    const h = toSize(height);

    if (h >= WINDOW_HEIGHT_EXPANDED_MIN) {
        return 'expanded';
    }

    return h >= WINDOW_HEIGHT_MEDIUM_MIN ? 'medium' : 'compact';
};

/**
 * Pure size-class descriptor for a window — the testable core of
 * {@link useWindowSizeClass}.
 *
 * @param {number} width Window width in dp.
 * @param {number} height Window height in dp.
 * @returns {{
 *   width: number, height: number,
 *   widthClass: 'compact'|'medium'|'expanded', heightClass: 'compact'|'medium'|'expanded',
 *   isCompact: boolean, isMedium: boolean, isExpanded: boolean, isMediumUp: boolean,
 *   isLandscape: boolean, isNearSquare: boolean, columns: 1|2|3
 * }} `isMediumUp`: width >= 600 (the tablet-style layouts); `isNearSquare`: aspect ratio
 *   within 1:1.35 (the unfolded inner screen of a foldable); `columns`: 1 / 2 / 3 for
 *   compact / medium / expanded.
 */
export const getWindowSizeClass = (width, height) => {
    const w = toSize(width);
    const h = toSize(height);
    const widthClass = getWidthClass(w);
    const longSide = Math.max(w, h);
    const shortSide = Math.min(w, h);

    return {
        width: w,
        height: h,
        widthClass,
        heightClass: getHeightClass(h),
        isCompact: widthClass === 'compact',
        isMedium: widthClass === 'medium',
        isExpanded: widthClass === 'expanded',
        isMediumUp: widthClass !== 'compact',
        isLandscape: w > h,
        isNearSquare: shortSide > 0 && longSide / shortSide <= 1.35,
        columns: widthClass === 'expanded' ? 3 : widthClass === 'medium' ? 2 : 1,
    };
};

/**
 * Live window-size class. Reads `useWindowDimensions`, so it follows fold /
 * unfold, rotation, split-screen and desktop/web resizes (unlike an import-time
 * `Dimensions.get('window')` snapshot, which goes stale on the first fold).
 *
 * @returns {ReturnType<typeof getWindowSizeClass>}
 */
export const useWindowSizeClass = () => {
    const {width, height} = useWindowDimensions();

    return useMemo(() => getWindowSizeClass(width, height), [width, height]);
};

/**
 * Width for a centred dialog / popup card: a share of the window, never wider
 * than `max`, so the same dialog is not a 700dp slab on an unfolded foldable or
 * tablet but still fills a phone.
 *
 * @param {number} windowWidth Current window width in dp.
 * @param {{max?: number, ratio?: number}} [options] `max` (default {@link MODAL_MAX_WIDTH}) and
 *   `ratio` of the window to use on narrow windows (default 0.92).
 * @returns {number} Width in dp.
 */
export const getModalWidth = (windowWidth, {max = MODAL_MAX_WIDTH, ratio = 0.92} = {}) => (
    Math.min(Math.round(toSize(windowWidth) * ratio), max)
);

/**
 * Width for centred page content: the window minus the horizontal gutters,
 * capped at `max`.
 *
 * @param {number} windowWidth Current window width in dp.
 * @param {{max?: number, gutter?: number}} [options] `max` (default {@link CONTENT_MAX_WIDTH_WIDE}) and
 *   `gutter` per side (default 0).
 * @returns {number} Width in dp.
 */
export const getContentWidth = (windowWidth, {max = CONTENT_MAX_WIDTH_WIDE, gutter = 0} = {}) => (
    Math.max(0, Math.min(toSize(windowWidth) - gutter * 2, max))
);

/**
 * Hook form of {@link getModalWidth} bound to the live window width.
 *
 * @param {{max?: number, ratio?: number}} [options] See {@link getModalWidth}.
 * @returns {number} Width in dp.
 */
export const useModalWidth = (options) => {
    const {width} = useWindowDimensions();
    const max = options?.max;
    const ratio = options?.ratio;

    return useMemo(() => getModalWidth(width, {max, ratio}), [width, max, ratio]);
};
